import React, { useState } from 'react';
import {
  DataGrid,
  GridColDef
} from '@mui/x-data-grid';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  ListItemSecondaryAction
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon, Warning as WarningIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { createApiClient } from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser, type UserInfo } from '../../hooks/useApi';

export const MarketsManagement: React.FC = () => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const api = createApiClient(authenticatedFetch);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [completingReportId, setCompletingReportId] = useState<number | null>(null);
  const { data: currentUser } = useCurrentUser() as { data: UserInfo | null | undefined };

  // Check if user has market_compass.admin permission
  const hasAdminPermission = currentUser?.permissions?.includes('market_compass.admin');
  const hasCustomizePermission = currentUser?.permissions?.includes('market_compass.customize');

  // Fetch markets based on permission level
  // Admins get all markets, customizers get only accessible markets
  const { data: marketsResponse, isLoading: loadingMarkets } = useQuery({
    queryKey: ['admin', 'markets', hasAdminPermission ? 'all' : 'accessible'],
    queryFn: async () => {
      const endpoint = hasAdminPermission
        ? '/api/marketcompass/admin/markets/all'
        : '/api/marketcompass/admin/markets/accessible';
      return api.get(endpoint);
    },
    enabled: hasAdminPermission || hasCustomizePermission
  });

  const marketsData = marketsResponse?.data?.markets || [];

  // Fetch pending market reports (only if user has admin permission)
  const { data: pendingReportsResponse } = useQuery({
    queryKey: ['admin', 'pending-market-reports'],
    queryFn: async () => {
      const response = await api.get('/api/marketcompass/admin/markets/pending-reports');
      return response.data;
    },
    enabled: hasAdminPermission // Only fetch if user has permission
  });

  const pendingReports = pendingReportsResponse?.data?.pendingReports || [];
  const pendingReportsCount = pendingReportsResponse?.data?.count || 0;

  // Mutation for marking a report as completed
  const markReportCompletedMutation = useMutation({
    mutationFn: async (queueId: number) => {
      const response = await api.put(`/api/marketcompass/admin/market-report-queue/${queueId}/complete`);
      return response.data;
    },
    onSuccess: () => {
      // Refetch pending reports after successful completion
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-market-reports'] });
      setCompletingReportId(null);
    },
    onError: (error: any) => {
      console.error('Failed to mark report as completed:', error);
      setCompletingReportId(null);
      // You could add a toast/snackbar here to show the error to the user
    }
  });

  const handleMarkComplete = (queueId: number) => {
    setCompletingReportId(queueId);
    markReportCompletedMutation.mutate(queueId);
  };

  // Filter out markets without IDs
  const marketsWithCounts = React.useMemo(() => {
    if (!marketsData || !Array.isArray(marketsData)) return [];

    return marketsData.filter((market: any) => market && market.id);
  }, [marketsData]);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Market',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'continent_name',
      headerName: 'Continent',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || 'N/A'}</Typography>
      )
    },
    {
      field: 'country_name',
      headerName: 'Country',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || 'N/A'}</Typography>
      )
    },
    {
      field: 'is_active',
      headerName: 'Is Active',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        params.value ? (
          <CheckIcon color="success" />
        ) : (
          <CloseIcon color="error" />
        )
      )
    }
  ];

  const isLoading = loadingMarkets;

  // Filter markets based on search term
  const filteredMarkets = React.useMemo(() => {
    if (!searchTerm) return marketsWithCounts;
    
    return marketsWithCounts.filter((market) => 
      market.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [marketsWithCounts, searchTerm]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Pending Reports Box - Only visible to market_compass.admin users */}
      {hasAdminPermission && (
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: pendingReportsCount === 0 ? '#e8f5e9' : '#fff9c4', // Green when no reports, yellow when pending
            borderLeft: pendingReportsCount === 0 ? '4px solid #4caf50' : '4px solid #fbc02d' // Green or yellow border
          }}
        >
          <Box display="flex" alignItems="center" mb={1}>
            {pendingReportsCount === 0 ? (
              <CheckIcon sx={{ color: '#4caf50', mr: 1 }} />
            ) : (
              <WarningIcon sx={{ color: '#f57f17', mr: 1 }} />
            )}
            <Typography variant="h6" fontWeight="bold">
              Pending MarketCompass Reports
            </Typography>
          </Box>

          {pendingReportsCount === 0 ? (
            <Typography variant="body2" color="text.secondary">
              There are no MarketCompass reports that need to be generated.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {pendingReportsCount} market{pendingReportsCount !== 1 ? 's' : ''} waiting for report generation:
              </Typography>
              <List dense sx={{ pl: 2 }}>
                {pendingReports.map((report: any) => (
                  <ListItem
                    key={report.queue_id}
                    sx={{
                      py: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                    onClick={() => navigate(`/admin/hub/marketcompass/markets/${report.market_id}`)}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="medium">
                          {report.market_name} ({report.country_name})
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" color="text.secondary" component="span" display="block">
                            Requested by {report.created_by_email} on {new Date(report.created_at).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="span" display="block" sx={{ mt: 0.5 }}>
                            <strong>Price:</strong> {
                              report.price_paid_cents
                                ? `${(report.price_paid_cents / 100).toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: report.currency || 'USD'
                                  })}`
                                : 'N/A'
                            }
                            {' • '}
                            <strong>Payment:</strong> {report.payment_method || 'Unknown'}
                          </Typography>
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="mark complete"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent navigation when clicking button
                          handleMarkComplete(report.queue_id);
                        }}
                        disabled={completingReportId === report.queue_id}
                        title="Mark as completed"
                        sx={{
                          color: 'success.main',
                          '&:hover': {
                            backgroundColor: 'success.light',
                            color: 'success.dark'
                          }
                        }}
                      >
                        {completingReportId === report.queue_id ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          <CheckCircleIcon />
                        )}
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Paper>
      )}

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search by Market Name..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />
      <DataGrid
        rows={filteredMarkets}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.id}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 }
          },
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }]
          }
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        disableColumnMenu
        onRowClick={(params) => {
          navigate(`/admin/hub/marketcompass/markets/${params.row.id}`);
        }}
        sx={{
          height: 650,
          '& .MuiDataGrid-row': {
            cursor: 'pointer'
          }
        }}
      />
    </Box>
  );
};